/**
 * Savings Goal Service — Budget Management (011)
 */

import { supabase, getCurrentUserId } from '../lib/supabase';
import {
  SavingsGoal,
  SavingsGoalSummary,
  CreateSavingsGoalInput,
  UpdateSavingsGoalInput,
  AddProgressInput,
  SavingsGoalServiceResponse,
} from '../types/savings-goal.types';
import {
  calculateGoalProgress,
  calculateDaysRemaining,
  deriveGoalOnTrackStatus,
} from '../utils/budget-calculations';
import { validateCreateSavingsGoal } from '../utils/savings-goal-validation';

function mapGoal(row: any): SavingsGoal {
  const target = parseFloat(row.target_amount);
  const current = parseFloat(row.current_amount);
  return {
    id: row.id,
    userId: row.user_id,
    name: row.name,
    categoryType: row.category_type,
    targetAmount: target,
    currentAmount: current,
    targetDate: row.target_date,
    priority: row.priority,
    monthlyTarget: row.monthly_target ? parseFloat(row.monthly_target) : undefined,
    percentComplete: calculateGoalProgress(current, target),
    daysRemaining: calculateDaysRemaining(row.target_date),
    onTrackStatus: deriveGoalOnTrackStatus(current, target, row.created_at, row.target_date),
    status: row.status,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

class SavingsGoalService {
  async getSummary(userId?: string): Promise<SavingsGoalServiceResponse<SavingsGoalSummary>> {
    try {
      const goals = await this.listGoals(userId);
      if (goals.error) return { error: goals.error };
      const active = (goals.data ?? []).filter((g) => g.status === 'active');
      const totalTarget = active.reduce((s, g) => s + g.targetAmount, 0);
      const totalSaved = active.reduce((s, g) => s + g.currentAmount, 0);
      const avgProgress = active.length
        ? Math.round(active.reduce((s, g) => s + g.percentComplete, 0) / active.length)
        : 0;
      const monthlyTargetTotal = active.reduce((s, g) => s + (g.monthlyTarget ?? 0), 0);
      return {
        data: {
          totalTargetAmount: totalTarget,
          activeGoalCount: active.length,
          totalSaved,
          averageProgressPercent: avgProgress,
          monthlyTargetTotal,
        },
      };
    } catch (e: any) {
      return { error: { code: 'UNKNOWN_ERROR', message: e.message } };
    }
  }

  async listGoals(userId?: string): Promise<SavingsGoalServiceResponse<SavingsGoal[]>> {
    try {
      const uid = userId ?? (await getCurrentUserId());
      if (!uid) return { error: { code: 'UNAUTHORIZED', message: 'Not authenticated' } };

      const { data, error } = await supabase
        .from('savings_goals')
        .select('*')
        .eq('user_id', uid)
        .neq('status', 'archived')
        .order('target_date', { ascending: true });

      if (error) return { error: { code: 'DB_ERROR', message: error.message } };
      return { data: (data ?? []).map(mapGoal) };
    } catch (e: any) {
      return { error: { code: 'UNKNOWN_ERROR', message: e.message } };
    }
  }

  async createGoal(input: CreateSavingsGoalInput, userId?: string): Promise<SavingsGoalServiceResponse<SavingsGoal>> {
    const parsed = validateCreateSavingsGoal(input);
    if (!parsed.success) {
      return { error: { code: 'VALIDATION_ERROR', message: parsed.error.issues[0]?.message ?? 'Invalid' } };
    }
    try {
      const uid = userId ?? (await getCurrentUserId());
      if (!uid) return { error: { code: 'UNAUTHORIZED', message: 'Not authenticated' } };

      const { data, error } = await supabase
        .from('savings_goals')
        .insert({
          user_id: uid,
          name: input.name,
          category_type: input.categoryType,
          target_amount: input.targetAmount,
          current_amount: input.startingAmount ?? 0,
          target_date: input.targetDate,
          priority: input.priority,
          monthly_target: input.monthlyTarget ?? null,
        })
        .select()
        .single();

      if (error || !data) return { error: { code: 'DB_ERROR', message: error?.message ?? 'Failed' } };
      return { data: mapGoal(data) };
    } catch (e: any) {
      return { error: { code: 'UNKNOWN_ERROR', message: e.message } };
    }
  }

  async updateGoal(id: string, input: UpdateSavingsGoalInput, userId?: string): Promise<SavingsGoalServiceResponse<SavingsGoal>> {
    try {
      const uid = userId ?? (await getCurrentUserId());
      if (!uid) return { error: { code: 'UNAUTHORIZED', message: 'Not authenticated' } };

      const updates: Record<string, unknown> = { updated_at: new Date().toISOString() };
      if (input.name) updates.name = input.name;
      if (input.categoryType) updates.category_type = input.categoryType;
      if (input.targetAmount) updates.target_amount = input.targetAmount;
      if (input.targetDate) updates.target_date = input.targetDate;
      if (input.priority) updates.priority = input.priority;
      if (input.monthlyTarget !== undefined) updates.monthly_target = input.monthlyTarget;
      if (input.startingAmount !== undefined) updates.current_amount = input.startingAmount;
      if (input.status) updates.status = input.status;

      const { data, error } = await supabase
        .from('savings_goals')
        .update(updates)
        .eq('id', id)
        .eq('user_id', uid)
        .select()
        .single();

      if (error || !data) return { error: { code: 'NOT_FOUND', message: error?.message ?? 'Not found' } };
      return { data: mapGoal(data) };
    } catch (e: any) {
      return { error: { code: 'UNKNOWN_ERROR', message: e.message } };
    }
  }

  async deleteGoal(id: string, userId?: string): Promise<SavingsGoalServiceResponse<{ deleted: true }>> {
    try {
      const uid = userId ?? (await getCurrentUserId());
      if (!uid) return { error: { code: 'UNAUTHORIZED', message: 'Not authenticated' } };

      const { error } = await supabase.from('savings_goals').delete().eq('id', id).eq('user_id', uid);
      if (error) return { error: { code: 'DB_ERROR', message: error.message } };
      return { data: { deleted: true } };
    } catch (e: any) {
      return { error: { code: 'UNKNOWN_ERROR', message: e.message } };
    }
  }

  async addProgress(input: AddProgressInput, userId?: string): Promise<SavingsGoalServiceResponse<SavingsGoal>> {
    try {
      const uid = userId ?? (await getCurrentUserId());
      if (!uid) return { error: { code: 'UNAUTHORIZED', message: 'Not authenticated' } };

      const { error: contribErr } = await supabase.from('savings_contributions').insert({
        goal_id: input.goalId,
        user_id: uid,
        amount: input.amount,
        contribution_date: input.contributionDate,
      });
      if (contribErr) return { error: { code: 'DB_ERROR', message: contribErr.message } };

      const { data, error } = await supabase
        .from('savings_goals')
        .select('*')
        .eq('id', input.goalId)
        .eq('user_id', uid)
        .single();

      if (error || !data) return { error: { code: 'NOT_FOUND', message: 'Goal not found' } };
      return { data: mapGoal(data) };
    } catch (e: any) {
      return { error: { code: 'UNKNOWN_ERROR', message: e.message } };
    }
  }
}

export const savingsGoalService = new SavingsGoalService();

export async function getSummary(userId?: string) {
  return savingsGoalService.getSummary(userId);
}
export async function listGoals(userId?: string) {
  return savingsGoalService.listGoals(userId);
}
export async function createGoal(input: CreateSavingsGoalInput, userId?: string) {
  return savingsGoalService.createGoal(input, userId);
}
export async function updateGoal(id: string, input: UpdateSavingsGoalInput, userId?: string) {
  return savingsGoalService.updateGoal(id, input, userId);
}
export async function deleteGoal(id: string, userId?: string) {
  return savingsGoalService.deleteGoal(id, userId);
}
export async function addProgress(input: AddProgressInput, userId?: string) {
  return savingsGoalService.addProgress(input, userId);
}
